import { AlertTriangle } from '@untitledui/icons';
import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { ExpandableMultiValueLabel } from '@/lib/crm/components/properties/labels/ExpandableMultiValueLabel';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { getEntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';

import { MergeAssociationsSummary } from './merge-associations';
import { useMergedPreviewObject } from './useMergedPreview';

export interface MergeStep3ReviewConfirmProps {
  canonicalObject: CrmObject | undefined;
  sourceObject: CrmObject | undefined;
  entityTypeName: string;
}

/**
 * Step 3 is a READ-ONLY review of the merge result. Every decision (property
 * conflicts, multi-value, association picks/dismissals) is made in Step 2; this
 * step only shows what the merged record will look like and confirms the
 * irreversible action. No inputs here. The merged record renders full-width so
 * it fills the review pane rather than a narrow left column.
 */
export function MergeStep3ReviewConfirm({
  canonicalObject,
  sourceObject,
  entityTypeName,
}: MergeStep3ReviewConfirmProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? 0;

  const { schema } = usePropertySchema(orgId, entityTypeName);
  const previewObject = useMergedPreviewObject(
    canonicalObject,
    sourceObject,
    schema
  );

  // Generic fallback — never a raw/truncated entity id.
  const displayName = previewObject
    ? getDisplayName(previewObject, 'Unnamed record')
    : '';

  const displayFields = useMemo(() => {
    const cfg = getEntityTypeConfig(entityTypeName);
    const keys = (cfg.detailFieldOrder ?? [])
      .filter(f => f.type === 'property')
      .map(f => f.key);
    return keys
      .filter(key => previewObject?.properties?.[key] !== undefined)
      .slice(0, 12);
  }, [entityTypeName, previewObject]);

  return (
    <div className='flex flex-col gap-6'>
      <section>
        <h2 className='mb-3 text-body font-semibold text-[var(--color-text-primary)]'>
          Merged record
        </h2>
        <div className='w-full rounded-lg border border-[var(--color-border-secondary)] p-4'>
          <div className='mb-3 text-heading-4 font-semibold text-[var(--color-text-primary)]'>
            {displayName}
          </div>
          <div className='grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3'>
            {displayFields.map(key => {
              const propSchema = schema[key];
              const val = previewObject?.properties?.[key];
              if (!val) {
                return null;
              }
              return (
                <div key={key} className='flex flex-col gap-0.5'>
                  <span className='text-body-small text-[var(--color-text-tertiary)]'>
                    {propSchema?.label ?? key}
                  </span>
                  <ExpandableMultiValueLabel
                    value={val.value}
                    propertyType={propSchema}
                    fontSize='sm'
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section>
        <h2 className='mb-3 text-body font-semibold text-[var(--color-text-primary)]'>
          Associations
        </h2>
        <MergeAssociationsSummary
          canonicalObject={canonicalObject}
          sourceObject={sourceObject}
          orgId={orgId}
        />
      </section>

      <div
        className='flex items-start gap-2 rounded-lg border border-[var(--color-error-300)] bg-[var(--color-error-50)] p-3'
        role='alert'
      >
        <AlertTriangle className='mt-0.5 h-5 w-5 text-[var(--color-error-600)]' />
        <div className='flex flex-col gap-1'>
          <p className='text-body-small font-semibold text-[var(--color-text-primary)]'>
            This action cannot be undone
          </p>
          <p className='text-body-small text-[var(--color-text-secondary)]'>
            The source record will be deleted. Associations and properties will
            be merged into the canonical record.
          </p>
        </div>
      </div>
    </div>
  );
}
